import _ from 'lodash';
import { semanticType, getCommands, getComponents, getProperties, getTelemetry, parse } from 'utils/dtdl';
import { sortLocale } from 'utils/string';

/**
 * Parse dtdl model into Admin Model to be able to view the
 * info
 */
export const contentToModel = (contentList) => {
  return _.map(contentList, (c) => {
    return {
      name: c.name,
      displayName: c.displayName,
      schema: c.schema,
      comment: c.comment,
      description: c.description,
      metaProperty: c.metaProperty,
      ...(semanticType(c['@type']) && { semanticType: semanticType(c['@type']) }),
      ...(c.unit && { semanticUnit: c.unit }),
      ...(c.writable && { writable: c.writable }),
      ...(c.request && { request: { request: c.request } }),
      ...(c.response && { response: { response: c.response } })
    };
  });
};

export const serializeDtdl = ({ modelInfo, telemetry, properties, commands, components, schemas }) => {
  const dtdlId = createModelId(modelInfo?.id, modelInfo?.version);
  const dtdl = {
    '@id': dtdlId,
    '@type': 'Interface',
    '@context': ['dtmi:dtdl:context;3', 'dtmi:bt:extension:metaProperty;1', 'dtmi:dtdl:extension:quantitativeTypes;1'],
    description: modelInfo?.description,
    displayName: modelInfo?.displayName,
    contents: [
      ..._.map(telemetry, (c) => {
        return serializeContent(dtdlId, c, 'Telemetry');
      }),
      ..._.map(properties, (c) => {
        return serializeContent(dtdlId, c, 'Property');
      }),
      ..._.map(commands, (c) => {
        return serializeContent(dtdlId, c, 'Command');
      }),
      ..._.map(components, (c) => {
        return serializeComponent(dtdlId, c, 'Component');
      })
    ],
    ...(!_.isEmpty(schemas) && {
      schemas: []
    })
  };
  return dtdl;
};

export const saveModel = ({ modelInfo, telemetry, properties, commands, components, schemas }) => {
  const dtdl = serializeDtdl({ modelInfo, telemetry, properties, commands, components, schemas });
  return {
    id: dtdl['@id'],
    dtdlModelId: dtdl['@id'],
    displayName: dtdl.displayName,
    modelData: [dtdl],
    modelType: modelInfo.modelType,
    description: modelInfo.description
  };

};
/**
 * Parse binding model into Admin Binding Model to be able to view the
 * info
 */
export const contentToBindingModel = (contentList) => {
  return _.map(contentList, (c) => {
    return {
      target: c.target,
      displayName: c.displayName,
      comment: c.comment,
      description: c.description
    };
  });
};

const serializeBase = (dtdlModelId, content, type) => {
  return {
    '@id': toContentId(dtdlModelId, content.name),
    '@type': (_.isEmpty(content.semanticType) && !content.metaProperty) ? type : _.compact([type, content.semanticType, content?.metaProperty ? 'MetaProperty' : null]),
    name: content.name,
    ...(!_.isEmpty(content.displayName) && { displayName: content.displayName }),
    ...(!_.isEmpty(content.description) && { description: content.description }),
    ...(!_.isEmpty(content.comment) && { comment: content.comment })
  };
};

export const serializeComponent = (dtdlModelId, content, type) => {
  return {
    ...serializeBase(dtdlModelId, content, type),
    schema: content.schema.id
  };
};

export const serializeContent = (dtdlModelId, content, type) => {
  return {
    ...serializeBase(dtdlModelId, content, type),
    schema: content.schema,
    ...(content.writable && { writable: content.writable }),
    ...(!_.isEmpty(content.semanticUnit) && { unit: content.semanticUnit }),
    ...(content.request && { ...content.request }),
    ...(content.response && { ...content.response })
  };
};


export const dtdlNameError = (name, currentNames) => {
  if (_.isEmpty(name)) {
    return true;
  }
  if (!_.isEmpty(_.filter(currentNames, n => n === name))) {
    return true;
  }
  if (!/^[a-zA-Z](?:[a-zA-Z0-9_]*[a-zA-Z0-9])?$/.test(name)) {
    return true;
  }
  return false;
};

export const dtdlIdError = (id) => {
  return !id || id.length === 0 || !/^[A-Za-z](?:[A-Za-z0-9_]*[A-Za-z0-9])?(?::[A-Za-z](?:[A-Za-z0-9_]*[A-Za-z0-9])?)*$/.test(id);
};

export const dtdlVersionError = (version) => {
  return !/^[1-9][0-9]{0,8}$/.test(version);
};

export const dtdlIdVersionError = (id) => {
  return !/^dtmi:[A-Za-z](?:[A-Za-z0-9_]*[A-Za-z0-9])?(?::[A-Za-z](?:[A-Za-z0-9_]*[A-Za-z0-9])?)*;[1-9][0-9]{0,8}$/.test(id);
};

// TODO update validation rules
export const BindingDescriptionIdError = (id) => {
  switch (id) {
    case id.includes(':_default:'):
      return false;
    case id.includes('__ui:'):
      return false;
    default:
      return false;
  }
};

export const dtdlIdConflict = () => {
  return false;
};

export const modelIdWithoutTemplateId = (modelId) => {
  return _.first(_.split(modelId, '.')); // model comes with a "template version that is not used"
};

export const parseModelId = (id) => {
  const l = id.replace('dtmi:', '').split(';');
  return { id: _.first(l), version: _.last(l) };
};

export const parseBindingId = (id) => {
  const regexp = /^btbi:(?<id>.*);(?<version>[0-9.]+)$/;
  const match = id.match(regexp);
  return { id: match?.groups.id ?? '', version: match?.groups.version ?? '1' };
};

export const createModelId = (name, version) => {
  return `dtmi:${name};${version}`;
};

export const createBindingId = (name, version) => {
  return `btbi:${name};${version}`;
};

export const createBindingIdFromModelId = (modelId, name) => {
  // TODO: update when DTDLv3 is introduced
  const dtmi = _.first(modelId.split('.'));
  return `btbi:${dtmi}:${name}__ui;1`;
};

export const parseContentId = (contentId) => {
  if (contentId == null) {
    return { modelId: null, name: null };
  }

  const regExp = /^(.+):_c:(.+);([0-9]+)$/;
  const match = contentId.match(regExp);
  if (match == null) {
    return { modelId: null, name: null };
  }
  return { modelId: `${match[1]};${match[3]}`, name: match[2] };
};

export const toContentId = (modelId, name) => {
  const m = modelId.split(';');
  return `${m[0]}:_c:${name};${m[1]}`;
};

export const envelopeToDtdlList = (sources) => {
  return _.map(sources, source => {
    return parse(source.modelData[0], source.modelData[0]);
  });
};

export const toBindingContentId = (bindingDescriptionId, name) => {
  const { id, version } = parseBindingId(bindingDescriptionId);
  return `btbi:${id}:_c:${name};${version}`;
};

export const parseBindingContentId = (bindingContentId) => {
  const regExp = /^(.+):_c:(.+);([0-9]+)$/;
  const match = bindingContentId.match(regExp);
  return { bindingModelId: `${match[1]};${match[3]}`, name: match[2] };
};

export const bindingDescriptionIdFromBindingContentId = (bindingContentId) => {
  const regExp = /^(.+):_c:(.+);([0-9]+)$/;
  const match = bindingContentId.match(regExp);
  return `${match[1]};${match[3]}`;
};

export const mapDtdlToAssetModel = (model, models) => {
  const parsedModel = parse(model, models);

  const telemetry = sortLocale(getTelemetry({ dtdlModel: parsedModel, component: '.' }), 'displayName');
  const [properties, metaProperties] = _.partition(sortLocale(getProperties({ dtdlModel: parsedModel, component: '.' }), 'displayName'), p => !p.metaProperty);
  const commands = sortLocale(getCommands({ dtdlModel: parsedModel, component: '.' }), 'displayName');
  const modules = sortLocale(getComponents({ model: parsedModel, pruneRoot: true }), 'displayName');
  const parsedModules = _.map(modules, module => {
    const t = sortLocale(getTelemetry({ dtdlModel: parsedModel, component: module.name }), 'displayName');
    const p = sortLocale(getProperties({ dtdlModel: parsedModel, component: module.name }), 'displayName');
    const c = sortLocale(getCommands({ dtdlModel: parsedModel, component: module.name }), 'displayName');
    const m = [];
    return {
      id: module.name,
      displayName: module.displayName ?? modules.name,
      telemetry: _.map(t, val => ({ ...val, componentName: module.name })),
      properties: _.map(p, val => ({ ...val, componentName: module.name })),
      metaProperties: _.map(m, val => ({ ...val, componentName: module.name })),
      commands: _.map(c, val => ({ ...val, componentName: module.name }))
    };
  });
  return {
    id: model['@id'],
    displayName: model.displayName,
    comment: model.comment ?? '',
    componentName: null,
    description: model.description ?? '',
    modules: parsedModules,
    telemetry: _.map(telemetry, val => ({ ...val, componentName: null })),
    properties: _.map(properties, val => ({ ...val, componentName: null })),
    metaProperties: _.map(metaProperties, val => ({ ...val, componentName: null })),
    commands: _.map(commands, val => ({ ...val, componentName: null }))
  };
};

export function getUniqueContentBindingDescriptionIds(assets) {
  const a = _.map(assets, asset => {
    const [, rest] = _.partition(asset.contentBindings, binding => _.includes(binding?.contentBindingDescriptionId, '_default'));
    const a = _.uniq(_.map(rest, r => bindingDescriptionIdFromBindingContentId(r.contentBindingDescriptionId)));
    return a;
  });
  const d = _.map(assets, asset => {
    const [_default] = _.partition(asset.contentBindings, binding => _.includes(binding?.contentBindingDescriptionId, '_default'));
    const d = _.uniq(_.map(_default, r => bindingDescriptionIdFromBindingContentId(r.contentBindingDescriptionId)));
    return d;
  });
  return { assetBindingIds: _.uniq(_.flatten(a)), defaultBindingIds: _.uniq(_.flatten(d)) };
}
